import React, { useState, useCallback } from "react";
import {
  Box,
  Snackbar,
  Alert,
  Drawer,
  useMediaQuery,
  IconButton,
  Toolbar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import KycTable from "./components/KycTable";
import ProfilePage from "./pages/Profile";
import Dashboard from "./pages/Dashboard";
import SettingsPage from "./pages/Settings";
import KYCDetailsPopup from "./popups/KYCDetailsPopup";
import useKycLogs from "./hooks/useKycLogs";
import { logoutApi } from "../Services/authService";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../store/reducers/auth";
import { useNavigate } from "react-router-dom";

const drawerWidth = 240;
const collapsedDrawerWidth = 60;

const AdminPortal = () => {
  const savedView = sessionStorage.getItem("currentView") || "Dashboard";
  const [currentView, setCurrentView] = useState(savedView);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false); // Mobile drawer toggle
  const [openPopup, setOpenPopup] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const { user } = useSelector((state) => state.auth);
  const { filteredLogs, kycLogs, loading, searchTerm, setSearchTerm,
          statusFilter, setStatusFilter, dateRange, setDateRange,
          fetchUsers, handleFiltersChange } = useKycLogs(user);

  const isMobile = useMediaQuery("(max-width:900px)");

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };
  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  // Logout handler
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const handleLogout = useCallback(async () => {
    const sourceUrl = sessionStorage.getItem("source");
    sessionStorage.removeItem("source");
    await logoutApi(user);
    dispatch(logoutUser());
    sourceUrl ? (window.location.href = sourceUrl) : navigate("/vkyc/login");
  }, [dispatch, navigate, user]);

  // Popup Details
  const handleViewDetails = (log) => {
    setSelectedLog(log);
    setOpenPopup(true);
  };

  const toggleMobileDrawer = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", bgcolor: "grey.50" }}>
      {/* Sidebar */}
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={toggleMobileDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
            },
          }}
        >
          <Sidebar
            currentView={currentView}
            setCurrentView={(v) => {
              setCurrentView(v);
              sessionStorage.setItem("currentView", v);
              if (v === "KYC Logs") fetchUsers();
              setMobileOpen(false); // Close drawer on mobile after selection
            }}
            collapsed={false}
            toggleCollapse={() => {}}
          />
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
            flexShrink: 0,
            [`& .MuiDrawer-paper`]: {
              width: sidebarCollapsed ? collapsedDrawerWidth : drawerWidth,
              transition: "width 0.3s ease",
            },
          }}
        >
          <Sidebar
            currentView={currentView}
            setCurrentView={(v) => {
              setCurrentView(v);
              sessionStorage.setItem("currentView", v);
              if (v === "KYC Logs") fetchUsers();
            }}
            collapsed={sidebarCollapsed}
            toggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
          />
        </Drawer>
      )}

      {/* Main Content */}
      <Box sx={{ flexGrow: 1, p: 1, overflowY: "auto" }}>
        {isMobile && (
          <Toolbar>
            <IconButton
              color="inherit"
              edge="start"
              onClick={toggleMobileDrawer}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        )}
        <TopBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          dateRange={dateRange}
          setDateRange={setDateRange}
          user={user}
          onLogout={handleLogout}
          onFilterChange={handleFiltersChange}
          currentView={currentView}
          setCurrentView={setCurrentView}
        />

        {currentView === "Dashboard" && <Dashboard />}
        {currentView === "Settings" && <SettingsPage />}
        {currentView === "Profile" && <ProfilePage user={user} />}
        {currentView === "KYC Logs" && (
          <KycTable
            loading={loading}
            filteredLogs={filteredLogs}
            kycLogs={kycLogs}
            onView={handleViewDetails}
          />
        )}
      </Box>

      {/* Snackbar Notification */}
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* KYC Popup */}
      {selectedLog && (
        <KYCDetailsPopup
          open={openPopup}
          onClose={() => setOpenPopup(false)}
          log={selectedLog}
          onUpdate={fetchUsers}
          onActionComplete={handleSnackbar}
        />
      )}
    </Box>
  );
};

export default AdminPortal;
